import React, { useState } from "react";
import { IntlProvider } from "react-intl";
import {
  ThemeProvider,
  createTheme,
  responsiveFontSizes,
} from "@mui/material/styles";
import { messages } from "./messages";

import { UserProvider } from "./Components/Context/UserProvider";
import Rutas from "./Routes/Routes";
import { ClientsProvider } from "./Components/Context/ClientsProvider";
import ClientRoutes from "./Routes/ClientRoutes";
import { BrowserRouter, useLocation } from "react-router-dom";
import { Fade, Grow } from "@mui/material";
import { PermissionProvider } from "./Components/Context/PermissionProvider";

function AppRouterContent({ setLocale }) {
  const location = useLocation();
  const isClientRoute = (location.pathname || "").startsWith("/client-sign");

  const Provider = isClientRoute ? ClientsProvider : UserProvider;
  const Routes = isClientRoute ? ClientRoutes : Rutas;

  return (
    <Provider>
      <PermissionProvider>
        <Routes setLocale={setLocale} />
      </PermissionProvider>
    </Provider>
  );
}

function App() {
  const [locale, setLocale] = useState("es");

  const theme = createTheme({
    typography: {
      fontSize: 14, // Tamaño base de referencia
      // Mantiene los variants estándar
      h1: { fontSize: "2rem", fontWeight: 600 },
      h2: { fontSize: "1.75rem", fontWeight: 600 },
      h3: { fontSize: "1.5rem", fontWeight: 600 },
      h4: { fontSize: "1.25rem", fontWeight: 600 },
      h5: { fontSize: "1.1rem", fontWeight: 600 },
      h6: { fontSize: "1rem", fontWeight: 600 },
      subtitle1: { fontSize: 15, fontWeight: 600 },
      subtitle2: { fontSize: 14, fontWeight: 600 },
      body1: { fontSize: "0.9rem" },
      body2: { fontSize: "0.8rem" },
      button: { fontSize: "0.85rem", textTransform: "none" },
      caption: { fontSize: "0.75rem" },
      overline: { fontSize: "0.7rem", letterSpacing: "0.05em" },
    },
    components: {
      MuiAutocomplete: {
        defaultProps: {
          PaperComponent: (props) => (
            <Fade in timeout={100}>
              <Fade in timeout={140}>
                <div
                  {...props}
                  style={{
                    background: "#FFFFFF",
                    // border: "1px solid #90CAF9", // azul suave de borde
                    boxShadow: `0px 8px 16px rgba(25, 118, 210, 0.12), 0px 4px 8px rgba(25, 118, 210, 0.08)`,
                    borderRadius: "10px", // esquinas suaves
                    overflow: "hidden", // evita alias en sombras
                    backdropFilter: "blur(8px)", // efecto elegante si se ve fondo
                    marginTop: "1px", // separación natural
                    zIndex: 1300,
                  }}
                />
              </Fade>
            </Fade>
          ),
        },
        styleOverrides: {
          paper: {
            borderRadius: 6,
            transition: "all 160ms ease-out",
          },
          option: {
            transition: "background-color 120ms ease",
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: "10px",
          },
        },
      },
      MuiDatePicker: {
        defaultProps: {
          slotProps: {
            textField: {
              size: "small",
              fullWidth: true,
            },
          },
        },
      },
      MuiDesktopDatePicker: {
        defaultProps: {
          slotProps: {
            textField: {
              size: "small",
              fullWidth: true,
            },
          },
        },
      },
      MuiDateTimePicker: {
        defaultProps: {
          slotProps: {
            textField: {
              size: "small",
              fullWidth: true,
            },
          },
        },
      },
      MuiMobileDatePicker: {
        defaultProps: {
          slotProps: {
            textField: {
              size: "small",
              fullWidth: true,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            fontSize: "14px",
          },
        },
      },
      //  Etiquetas de los campos
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "14px",
          },
        },
      },
      //  Labels de formularios (radios, checkboxes, etc.)
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: "14px",
          },
        },
      },
      //  Tipografías (Typography)
      MuiTypography: {
        styleOverrides: {
          root: {
            fontSize: "14px",
          },
        },
      },
      // ⬇ Opciones de los Select
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: "14px",
            transition: "background-color 120ms ease",
          },
        },
      },

      MuiMenu: {
        defaultProps: {
          TransitionComponent: Fade,
          transitionDuration: {
            enter: 120,
            exit: 100,
          },
          PaperProps: {
            sx: {
              background: "#FFFFFF",
              boxShadow:
                "0px 8px 16px rgba(25, 118, 210, 0.12), 0px 4px 8px rgba(25, 118, 210, 0.08)",
              borderRadius: "10px",
              overflow: "hidden",
              backdropFilter: "blur(8px)",
              marginTop: "4px",
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: "14px",
            paddingTop: "8.5px",
            paddingBottom: "8.5px",
          },
          icon: {
            color: "rgba(0, 0, 0, 0.6)",
          },
        },
      },

      //  Etiquetas de Radio y Checkbox
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginRight: "20px", // o el espaciado que prefieras
          },
          label: {
            fontSize: "13px",
          },
        },
      },
      //  Botones
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: "10px",
            textTransform: "uppercase",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "10px",
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={responsiveFontSizes(theme)}>
      <IntlProvider locale={locale} messages={messages[locale]}>
        <BrowserRouter>
          <AppRouterContent setLocale={setLocale} />
        </BrowserRouter>
      </IntlProvider>
    </ThemeProvider>
  );
}

export default App;
